import { Input, cn } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { type CampaignFormValues } from "../campaign-form";
import { FormFieldLabel } from "../../shared/form-field-label";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type ProductsStepProps = {
  errorMessage?: string;
  errorField?: string;
  showError?: boolean;
  onDismissError: () => void;
};

export function ProductsStep({
  errorMessage,
  errorField,
  showError = false,
  onDismissError,
}: ProductsStepProps) {
  const { values, setFieldValue } = useFormikContext<CampaignFormValues>();

  return (
    <section className="tw:max-w-4xl">
      <StepHeader
        title="Select Products"
        description="Choose which products from your catalog are eligible for this campaign. Only in-stock SKUs are shown - sold-out products are suppressed automatically."
      />

      <StepAlert
        show={showError}
        message={errorMessage}
        onDismiss={onDismissError}
      />

      <div className="tw:mt-8 tw:max-w-xl">
        <FormFieldLabel
          htmlFor="minimumPrice"
          helperText="The minimum price a product must have to be eligible for a campaign."
        >
          Minimum price
        </FormFieldLabel>
        <div
          className={cn(
            "tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:border-input tw:px-3 tw:dark:bg-input/30",
            showError &&
              errorField === "minimumPrice" &&
              "tw:border-destructive tw:focus-within:ring-1 tw:focus-within:ring-destructive/20",
          )}
        >
          <span className="tw:text-sm tw:text-muted-foreground/70">$</span>
          <Input
            id="minimumPrice"
            name="minimumPrice"
            value={values.minimumPrice}
            onChange={(event) => {
              const nextValue = event.target.value.replace(/[^0-9.]/g, "");
              const [wholePart, ...decimalParts] = nextValue.split(".");
              const normalizedValue =
                decimalParts.length > 0
                  ? `${wholePart}.${decimalParts.join("")}`
                  : wholePart;

              setFieldValue("minimumPrice", normalizedValue);
            }}
            placeholder="25"
            inputMode="decimal"
            aria-label="Minimum price"
            aria-invalid={showError && errorField === "minimumPrice"}
            className="tw:border-0 tw:bg-transparent! tw:px-0 tw:shadow-none tw:placeholder:text-muted-foreground/50 focus-visible:tw:ring-0"
          />
        </div>
      </div>
    </section>
  );
}
